import { useHistory } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import WebDatatable from 'app/shared/datatable/web/datatable';
import MobileDatatable from 'app/shared/datatable/mobile/datatable';

import {
  addSentrySearchResultsToStore,
  addManySentriesToStore,
  removeOneSentryFromStore
} from 'store/actions/sentry';
import { setPageTitle } from 'store/actions/header';
import { Button } from 'app/shared/button';
import { useSentryService } from 'hooks/sentry';
import GmModal from 'app/shared/modal/modal';
import { SentryCreation } from '../create';
import { Badge } from 'app/shared/badge';
import { toast } from 'react-toastify';

const SentryList = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { deleteById, read, search } = useSentryService();
  const { is_mobile_view } = useSelector((state) => state.metadata);
  const stored_sentries = useSelector((state) => state.sentries);

  const [selected_sentry, setSelectedSentry] = useState({});
  const [show_creation_modal, setShowCreationModal] = useState(false);
  const [is_search_mode, setSearchMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sentries, setSentries] = useState([]);
  const [total_sentries, setTotalSentries] = useState(0);

  useEffect(() => {
    dispatch(setPageTitle([{ title: 'Sentries', path: '.' }]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSentries(Object.values(stored_sentries || {}));
  }, [stored_sentries]);

  const config = {
    actions: {
      single: ['View', 'Edit', 'Delete']
    },
    allow_bulk_action: true,
    css: {},
    fields: [
      {
        title: 'URL',
        key: 'url',
        isTitle: true,
        formatter: (value) => (value.length > 50 ? `${value.substr(0, 50)}...` : value)
      },
      {
        title: 'Status',
        key: 'status',
        formatter: (value) => <Badge text={value} type={value === 'up' ? 'green' : 'red'} />
      },
      {
        title: 'Creation date',
        key: 'created_on',
        formatter: (value) => new Date(value).toDateString(),
        isMetadata: true
      }
    ],
    is_search_mode,
    items: sentries,
    search_key: 'name',
    search_text: ''
  };

  const handleDatatableAction = (payload) => {
    const { name, type, data } = payload;

    if (type === 'single') {
      switch (name) {
        case 'Edit':
          setSelectedSentry(data);
          setShowCreationModal(true);
          break;
        case 'Delete':
          handleDeletion(data.id);
          break;
        default:
          history.push(`/sentries/${data.id}`);
      }
    }
  };

  const handleDeletion = async (sentry_id) => {
    setLoading(true);
    const response = await deleteById(sentry_id);
    if (response) {
      toast.success('Sentry deleted successfully');
      dispatch(removeOneSentryFromStore(sentry_id));
    }
    setLoading(false);
  };

  const handleItemClick = (payload) => {
    setSelectedSentry(payload);
    setShowCreationModal(true);
  };

  const handleDataRequest = async (page, population = 50) => {
    try {
      setLoading(true);
      const { sentries, size } = await read({
        query_string: `page=${page}&population=${population}&sort_by=-created_on`
      });

      const parsed_sentries = sentries.map((sentry) => ({
        ...sentry,
        meta: sentry.metadata || {}
      }));
      dispatch(addManySentriesToStore(parsed_sentries));
      setTotalSentries(size);
    } catch (e) {
      dispatch(addManySentriesToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const handleSearchRequest = async (keys, keyword, page, population = 50) => {
    if (!keys) return;
    try {
      setLoading(true);
      const { sentries, size } = await search(keys, keyword, {
        query_string: `page=${page}&population=${population}&sort_by=-created_on`
      });

      const parsed_sentries = sentries.map((sentry) => ({
        ...sentry,
        meta: sentry.metadata || {}
      }));

      setTotalSentries(size);
      if (page === 0) return dispatch(addSentrySearchResultsToStore(parsed_sentries));
      dispatch(addManySentriesToStore(parsed_sentries));
    } catch (e) {
      dispatch(addManySentriesToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const table_actions = (
    <>
      <Button icon_name="add" text="Create Sentry" onClick={() => setShowCreationModal(true)} />
    </>
  );

  return (
    <div className="content-area">
      {is_mobile_view ? (
        <MobileDatatable
          config={config}
          action={handleDatatableAction}
          onClick={handleItemClick}
          onListModeChange={setSearchMode}
          onDataRequest={handleDataRequest}
          onSearchRequest={handleSearchRequest}
        />
      ) : (
        <WebDatatable
          config={{
            ...config,
            total_count: total_sentries
          }}
          action={handleDatatableAction}
          onClick={handleItemClick}
          checkbox
          loading_data={loading}
          table_actions={table_actions}
          onDataRequest={handleDataRequest}
          onSearchRequest={handleSearchRequest}
        />
      )}
      <GmModal
        title={`${selected_sentry.id ? 'Edit Sentry' : 'Create Sentry'}`}
        show_title
        show_modal={show_creation_modal}
        onClose={() => setShowCreationModal(false)}
      >
        <SentryCreation initial_data={selected_sentry} />
      </GmModal>
    </div>
  );
};

export default SentryList;
